import type { Board, GeometryElement } from 'jsxgraph';
import type { ActionResult, TransitionProfile } from '../../../model/widget-action.model';
import { computeBoundingBoxForTargets, resolveTarget } from '../util/target-resolver';
import type { JsxgraphThemeService } from '../jsxgraph-theme.service';
import type { AnimationResult, ParamHandle, ParameterizedElement } from './action.model';
import { animateValue } from './action-executor';
import { scaleBoundingBox } from './animation';
import { StyleResolverRegistry } from './style';

/**
 * Interface for JSXGraph widgets that can execute built-in actions.
 * JsxgraphBaseWidget implements this interface, allowing actions to receive
 * the widget instance directly instead of a context bag.
 */
export interface BuiltInActionContext {
  readonly board: Board | null;
  readonly objects: Map<string, GeometryElement>;
  readonly parameterizedElements: Map<string, ParameterizedElement>;
  readonly isDestroyed: boolean;
  readonly themeService: JsxgraphThemeService;
  getRegionBoundingBox(region: string | number): [number, number, number, number] | null;
  animateBoundingBox(bbox: [number, number, number, number], transition?: TransitionProfile): Promise<void>;
  /**
   * Resolve a param path to a read/write handle.
   * Each widget decides how to interpret paths (e.g., 'f.a', 'k', 't2.scale').
   * Returns null if the path cannot be resolved.
   */
  resolveParam(path: string): ParamHandle | null;
  /** Called after each animation step in UPDATE_PARAM. Subclasses can override for per-frame reactions. */
  onParamAnimationStep?(): void;
}

/**
 * Focus on a region by animating to its bounding box.
 *
 * Supports multiple region formats:
 * - String element path: "f-roots" → auto-computes bounding box from element objects
 * - Explicit bounding box array: [-5, 5, 5, -5]
 * - Object with type:
 *   - { type: 'element', id: 'f-roots' } → focus on element
 *   - { type: 'point', x: 0, y: -4, radius: 3 } → focus on point with radius
 *   - { type: 'rect', left: -5, top: 5, right: 5, bottom: -5 } → explicit rect
 * - Named region (widget-defined via getRegionBoundingBox override)
 */
export async function focusRegion(
  ctx: BuiltInActionContext,
  payload?: Record<string, unknown>,
  transition?: TransitionProfile,
): Promise<ActionResult> {
  const region = payload?.['region'];
  const padding = (payload?.['padding'] as number) ?? 2;

  if (region === undefined || region === null) {
    return { success: false, error: 'Missing region in payload' };
  }
  if (!ctx.board) {
    return { success: false, error: 'Board not initialized' };
  }

  let bbox: [number, number, number, number] | null = null;

  // 1. Explicit bounding box array
  if (Array.isArray(region) && region.length === 4) {
    bbox = region as [number, number, number, number];
  }
  // 2. String path → try as element target, then as named region
  else if (typeof region === 'string') {
    // First try as element target path
    const resolution = resolveTarget(region, ctx.objects);
    if (resolution.objects.length > 0) {
      bbox = computeBoundingBoxForTargets(resolution.objects, padding);
    }
    // Fall back to widget-defined named regions
    if (!bbox) {
      bbox = ctx.getRegionBoundingBox(region);
    }
  }
  // 3. Object with explicit type
  else if (typeof region === 'object' && region !== null) {
    const regionObj = region as Record<string, unknown>;

    if (regionObj['type'] === 'element') {
      const resolution = resolveTarget(regionObj['id'] as string, ctx.objects);
      bbox = computeBoundingBoxForTargets(resolution.objects, padding);
    } else if (regionObj['type'] === 'point') {
      const x = regionObj['x'] as number;
      const y = regionObj['y'] as number;
      const r = (regionObj['radius'] as number) ?? padding;
      bbox = [x - r, y + r, x + r, y - r];
    } else if (regionObj['type'] === 'rect') {
      bbox = [
        regionObj['left'] as number,
        regionObj['top'] as number,
        regionObj['right'] as number,
        regionObj['bottom'] as number,
      ];
    }
  }

  if (!bbox) {
    return { success: false, error: `Could not resolve region: ${JSON.stringify(region)}` };
  }

  await ctx.animateBoundingBox(bbox, transition);
  return { success: true };
}

export async function transformView(
  ctx: BuiltInActionContext,
  payload?: Record<string, unknown>,
  transition?: TransitionProfile,
): Promise<ActionResult> {
  const mode = payload?.['mode'] as string;
  const factor = (payload?.['factor'] as number) ?? 1.1;

  if (!ctx.board) {
    return { success: false, error: 'Board not initialized' };
  }

  const currentBbox = ctx.board.getBoundingBox();
  let newBbox: [number, number, number, number];

  switch (mode) {
    case 'zoom_in':
      newBbox = scaleBoundingBox(currentBbox, 1 / factor);
      break;
    case 'zoom_out':
      newBbox = scaleBoundingBox(currentBbox, factor);
      break;
    default:
      return { success: false, error: `Unknown transform mode '${mode}'` };
  }

  await ctx.animateBoundingBox(newBbox, transition);
  return { success: true };
}

/**
 * Patch display styles on target element(s) using theme-aware resolution.
 *
 * Target supports element path resolution:
 * - "f"           → All objects for element "f"
 * - "f.0"         → Single object f-0
 * - "f-roots"     → All root point objects
 * - "f-roots.1"   → Single object f-roots-1
 *
 * Style keys are resolved through StyleResolverRegistry:
 * - curve: { color, strokeWidth, dash, visible, opacity }
 * - point: { color, strokeColor, size, visible, opacity }
 * - line: { color, strokeWidth, dash, visible }
 * - polygon: { color, fillColor, strokeColor, strokeWidth, opacity, visible }
 * - label: { color, fontSize, visible }
 * - angle: { color, strokeColor, radius, visible }
 * - circle: { color, fillColor, strokeColor, strokeWidth, opacity, visible }
 */
export function patchDisplay(ctx: BuiltInActionContext, payload?: Record<string, unknown>): ActionResult {
  if (!payload) {
    return { success: false, error: 'Missing payload' };
  }

  // Detect format: if payload has a 'target' key, use single-target mode.
  // Otherwise, treat as batch mode where each top-level key is an elementId.
  if ('target' in payload) {
    return patchDisplaySingle(ctx, payload['target'] as string, payload);
  }

  // Batch mode: { "f": { curve: { color: "green" } }, "lbl": { label: { text: "..." } } }
  let anySuccess = false;
  for (const [elementId, styles] of Object.entries(payload)) {
    if (typeof styles === 'object' && styles !== null) {
      const result = patchDisplaySingle(ctx, elementId, styles as Record<string, unknown>);
      if (result.success) {
        anySuccess = true;
      }
    }
  }

  return anySuccess ? { success: true } : { success: false, error: 'No elements patched' };
}

function patchDisplaySingle(
  ctx: BuiltInActionContext,
  targetPath: string,
  styles: Record<string, unknown>,
): ActionResult {
  if (!targetPath) {
    return { success: false, error: 'Missing target in payload' };
  }

  // Resolve target to objects
  const resolution = resolveTarget(targetPath, ctx.objects);

  if (resolution.objects.length === 0) {
    return { success: false, error: `No objects found for target '${targetPath}'` };
  }

  // Collect all resolved attributes
  const attrs: Record<string, unknown> = {};
  // Label config can appear at top level or nested inside a style key (e.g., line.label).
  // Nested labels match how display configs work at creation time (e.g., curve: { label: {...} }).
  let labelConfig: Record<string, unknown> | undefined;

  for (const [key, value] of Object.entries(styles)) {
    if (key === 'target') {
      continue;
    }

    // Top-level label — highest priority
    if (key === 'label') {
      if (typeof value === 'object' && value !== null) {
        labelConfig = value as Record<string, unknown>;
      }
      continue;
    }

    // Handle top-level simple attributes
    if (key === 'visible') {
      attrs['visible'] = value;
      continue;
    }

    // Look up resolver for this style key
    const resolver = StyleResolverRegistry.get(key);
    if (resolver && typeof value === 'object' && value !== null) {
      const styleObj = value as Record<string, unknown>;

      // Extract nested label from style keys (e.g., line: { label: { color: 'sky' } })
      // This matches how display configs work at creation time.
      if (!labelConfig && typeof styleObj['label'] === 'object' && styleObj['label'] !== null) {
        labelConfig = styleObj['label'] as Record<string, unknown>;
      }

      const resolved = resolver.resolve(styleObj, ctx.themeService);
      Object.assign(attrs, resolved);
    }
  }

  // Handle label config updates via reconfigure — text, color, variant, offset live in the
  // label's config (not as JSXGraph attributes). reconfigure() recomputes derived styles and
  // triggers a board update.
  if (labelConfig) {
    for (const obj of resolution.objects) {
      const reconfigure = (obj as unknown as { reconfigure?: (u: Record<string, unknown>) => void }).reconfigure;
      if (reconfigure) {
        reconfigure(labelConfig);
      }
    }
  }

  // Apply resolved JSXGraph attributes to all matching objects
  if (Object.keys(attrs).length > 0) {
    for (const obj of resolution.objects) {
      (obj as unknown as { setAttribute: (a: Record<string, unknown>) => void }).setAttribute(attrs);
    }
  }

  return { success: true };
}

/**
 * Temporarily highlight elements with theme-aware styles, then restore.
 *
 * Uses the same style resolver system as PATCH_DISPLAY.
 * After the duration, restores original styles.
 *
 * @example
 * // Highlight roots with green color for 1 second
 * {
 *   "action": "HIGHLIGHT",
 *   "payload": {
 *     "target": "f-roots",
 *     "point": { "color": "green", "size": 12 }
 *   },
 *   "transition": { "duration": 1000 }
 * }
 */
export async function highlight(
  ctx: BuiltInActionContext,
  payload?: Record<string, unknown>,
  transition?: TransitionProfile,
): Promise<ActionResult> {
  const targetPath = payload?.['target'] as string;
  if (!targetPath) {
    return { success: false, error: 'Missing target in payload' };
  }

  const duration = transition?.duration ?? 500;

  // Resolve target to objects
  const resolution = resolveTarget(targetPath, ctx.objects);
  if (resolution.objects.length === 0) {
    return { success: false, error: `No objects found for target '${targetPath}'` };
  }

  // Collect highlight styles from payload using style resolvers
  const highlightAttrs: Record<string, unknown> = {};
  if (payload) {
    for (const [key, value] of Object.entries(payload)) {
      if (key === 'target') {
        continue;
      }
      if (key === 'visible') {
        highlightAttrs['visible'] = value;
        continue;
      }
      const resolver = StyleResolverRegistry.get(key);
      if (resolver && typeof value === 'object' && value !== null) {
        Object.assign(highlightAttrs, resolver.resolve(value as Record<string, unknown>, ctx.themeService));
      }
    }
  }

  // If no highlight styles specified, use a default effect (thicker stroke)
  if (Object.keys(highlightAttrs).length === 0) {
    highlightAttrs['strokeWidth'] = 4;
  }

  // Capture original styles for the properties we're changing
  const originalStyles: Map<GeometryElement, Record<string, unknown>> = new Map();
  const styleKeys = Object.keys(highlightAttrs);

  for (const obj of resolution.objects) {
    const original: Record<string, unknown> = {};
    for (const key of styleKeys) {
      // Access the visProp object where JSXGraph stores visual properties
      const visProp = (obj as unknown as { visProp?: Record<string, unknown> }).visProp;
      if (visProp) {
        original[key] = visProp[key.toLowerCase()];
      }
    }
    originalStyles.set(obj, original);

    // Apply highlight styles
    (obj as unknown as { setAttribute: (a: Record<string, unknown>) => void }).setAttribute(highlightAttrs);
  }

  // Wait for duration
  await new Promise((resolve) => setTimeout(resolve, duration));

  // Restore original styles
  for (const obj of resolution.objects) {
    const original = originalStyles.get(obj);
    if (original && Object.keys(original).length > 0) {
      (obj as unknown as { setAttribute: (a: Record<string, unknown>) => void }).setAttribute(original);
    }
  }

  return { success: true };
}

export async function resetView(
  ctx: BuiltInActionContext,
  defaultBbox: [number, number, number, number],
  transition?: TransitionProfile,
): Promise<ActionResult> {
  if (!ctx.board) {
    return { success: false, error: 'Board not initialized' };
  }
  await ctx.animateBoundingBox(defaultBbox, transition);
  return { success: true };
}

/**
 * Update one or more parameters with animation.
 *
 * Delegates param path resolution to the widget via `ctx.resolveParam()`.
 * Each widget decides how to interpret param paths (e.g., 'f.a', 'k', 't2.scale').
 *
 * Single param:  { param: string, value: number }
 * Batch params:  { params: Record<string, number> }  — all animate in parallel
 */
export async function updateParam(
  ctx: BuiltInActionContext,
  payload?: Record<string, unknown>,
  transition?: TransitionProfile,
): Promise<ActionResult> {
  if (!ctx.board) {
    return { success: false, error: 'Board not initialized' };
  }

  if (!payload) {
    return { success: false, error: 'Missing payload' };
  }

  const defaultTransition: TransitionProfile = { duration: 0, easing: 'linear', type: 'none' };

  // Batch format: { params: { "pt.x": -2, "pt.y": -1 } }
  if ('params' in payload) {
    const params = payload['params'] as Record<string, number>;
    const animations = Object.entries(params).map(([paramPath, targetValue]) => {
      const handle = ctx.resolveParam(paramPath);
      if (!handle) {
        return Promise.resolve({ aborted: false } as AnimationResult);
      }
      return animateValue({
        startValue: handle.get(),
        endValue: targetValue,
        transition: transition ?? defaultTransition,
        onStep: (interpolated) => {
          handle.set(interpolated);
          ctx.onParamAnimationStep?.();
        },
        shouldAbort: () => ctx.isDestroyed || !ctx.board,
      });
    });

    const results = await Promise.all(animations);
    if (results.some((r) => r.aborted)) {
      return { success: false, error: 'Animation aborted' };
    }
    return { success: true };
  }

  // Single param format: { param: "pt.x", value: -2 }
  if (!('param' in payload) || !('value' in payload)) {
    return { success: false, error: 'Missing param or value in payload' };
  }

  const paramPath = payload['param'] as string;
  const value = payload['value'] as number;

  const handle = ctx.resolveParam(paramPath);
  if (!handle) {
    return { success: false, error: `Cannot resolve param '${paramPath}'` };
  }

  const startValue = handle.get();

  const result: AnimationResult = await animateValue({
    startValue,
    endValue: value,
    transition: transition ?? defaultTransition,
    onStep: (interpolated) => {
      handle.set(interpolated);
      ctx.onParamAnimationStep?.();
    },
    shouldAbort: () => ctx.isDestroyed || !ctx.board,
  });

  if (result.aborted) {
    return { success: false, error: 'Animation aborted' };
  }

  return { success: true };
}
